import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sharesApi, type ShareClassDto, type ShareOperationDto, type ShareholderDto } from '../api/shares';
import MoneyCell from '../components/MoneyCell';
import LoadingSpinner from '../components/LoadingSpinner';

/**
 * D7 — AKCIJOS: klasių kortelė, operacijų registras ir akcininkų registras
 * (Σ iki datos su ValueTotal = Σ × nominalas). Registras statistinis —
 * kapitalo įrašai žurnale rankiniai (legacy elgesys).
 */
export default function Shares() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'classes' | 'operations' | 'shareholders'>('shareholders');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data: classes } = useQuery({ queryKey: ['share-classes'], queryFn: sharesApi.classes });
  const { data: operations } = useQuery({
    queryKey: ['share-operations'],
    queryFn: () => sharesApi.operations({}),
  });
  const { data: holders } = useQuery({
    queryKey: ['share-holders', asOf],
    queryFn: () => sharesApi.shareholders(asOf),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['share-classes'] });
    queryClient.invalidateQueries({ queryKey: ['share-operations'] });
    queryClient.invalidateQueries({ queryKey: ['share-holders'] });
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      refresh();
    } catch (e: any) {
      setError(e.message || 'Nepavyko');
    } finally {
      setBusy(false);
    }
  };

  const TABS = [
    { key: 'shareholders', label: 'Akcininkai' },
    { key: 'operations', label: 'Operacijos' },
    { key: 'classes', label: 'Klasės' },
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Akcijos</h1>
          <p className="text-sm text-gray-500">
            Statistinis registras — kapitalo įrašai žurnale registruojami rankiniu būdu.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              tab === t.key
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      {tab === 'shareholders' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0.5rem' }}>
            <label>
              Iki:{' '}
              <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
            </label>
            <span className="text-sm text-gray-500">{holders?.length ?? 0} akcininkų eilutės</span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Akcininkas</th>
                <th>Asmens kodas</th>
                <th>Klasė</th>
                <th style={{ textAlign: 'right' }}>Kiekis</th>
                <th style={{ textAlign: 'right' }}>Nominalas</th>
                <th style={{ textAlign: 'right' }}>Vertė iš viso</th>
                <th>Įsigyta</th>
              </tr>
            </thead>
            <tbody>
              {(holders ?? []).map((h: ShareholderDto) => (
                <tr key={`${h.personId}-${h.classId}`}>
                  <td>{h.personName}</td>
                  <td>{h.personCode || '—'}</td>
                  <td>{h.className}</td>
                  <td style={{ textAlign: 'right' }}>{h.amount}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={h.valuePerUnit} /></td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={h.valueTotal} /></td>
                  <td>{h.acquisitionDate?.slice(0, 10) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={3}><strong>Viso</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{(holders ?? []).reduce((s, h) => s + h.amount, 0)}</strong></td>
                <td></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={(holders ?? []).reduce((s, h) => s + h.valueTotal, 0)} /></strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {tab === 'operations' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Registruota</th>
                <th>Dokumentas</th>
                <th>Numeris</th>
                <th>Eilutės</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(operations ?? []).map((o: ShareOperationDto) => (
                <tr key={o.id}>
                  <td>{o.registrationDate.slice(0, 10)}</td>
                  <td>{o.documentDate.slice(0, 10)} {o.documentName}</td>
                  <td>{o.documentNumber}</td>
                  <td>
                    {o.details.map(d => (
                      <div key={d.id} className="text-xs">
                        {d.itemType === 0 ? 'įsigijimas' : 'perdažymas'} — {d.className}: {d.quantity} ({d.personName})
                      </div>
                    ))}
                  </td>
                  <td>
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() => run(() => sharesApi.deleteOperation(o.id))}
                    >
                      Trinti
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'classes' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Pavadinimas</th>
                <th>SAFT kodas</th>
                <th style={{ textAlign: 'right' }}>Nominalas</th>
                <th style={{ textAlign: 'right' }}>Kapitalo sąskaita</th>
                <th style={{ textAlign: 'right' }}>Akcijų kiekis</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(classes ?? []).map((c: ShareClassDto) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.saftCode}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={c.valuePerUnit} /></td>
                  <td style={{ textAlign: 'right' }}>{c.account || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{c.shareCount}</td>
                  <td>
                    {!c.isInUse && (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() => run(() => sharesApi.deleteClass(c.id))}
                      >
                        Trinti
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0.5rem' }}>
            <NewClassRow onSave={cmd => run(() => sharesApi.saveClass(cmd))} disabled={busy} />
          </div>
        </div>
      )}
    </div>
  );
}

function NewClassRow({ onSave, disabled }: {
  onSave: (cmd: { id: number; name: string; valuePerUnit: number; saftCode: string; account: number; description: string; saftDescription: string | null }) => void;
  disabled: boolean;
}) {
  const [name, setName] = useState('');
  const [nominal, setNominal] = useState('0.29');
  const [saftCode, setSaftCode] = useState('PP');
  const [account, setAccount] = useState('');

  return (
    <>
      <input placeholder="Pavadinimas" value={name} onChange={e => setName(e.target.value)} style={{ width: '10rem' }} />
      <input placeholder="Nominalas" value={nominal} onChange={e => setNominal(e.target.value)} style={{ width: '6rem' }} />
      <select value={saftCode} onChange={e => setSaftCode(e.target.value)}>
        <option value="PP">PP</option>
        <option value="PRV">PRV</option>
      </select>
      <input placeholder="Sąskaita" value={account} onChange={e => setAccount(e.target.value)} style={{ width: '6rem' }} />
      <button
        disabled={disabled}
        onClick={() => {
          onSave({ id: 0, name, valuePerUnit: Number(nominal), saftCode, account: Number(account || 0), description: '', saftDescription: null });
          setName('');
        }}
      >
        {disabled ? <LoadingSpinner /> : '+ Klasė'}
      </button>
    </>
  );
}
